import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useText } from "@/lib/useText";

const clean = (s) => s.replace(/[\p{Extended_Pictographic}\p{Emoji_Modifier}\u2640-\u2642\u200d\ufe0f┃|]/gu, "").trim();
const hex = (c) => (c ? `#${c.toString(16).padStart(6, "0")}` : null);

const STATUS = {
  online: { cls: "bg-emerald-400", label: "Online" },
  idle: { cls: "bg-amber-400", label: "Ausente" },
  dnd: { cls: "bg-red-500", label: "Não incomodar" },
  offline: { cls: "bg-eden-muted/40", label: "Offline" },
};

export function StaffTeam() {
  const { content } = useContent();
  const t = useText();
  const staff = content.staff;
  const [data, setData] = useState(null);
  const key = staff?.auto === false ? (staff?.role_ids || []).join(",") : `auto:${staff?.min_role_id || ""}`;

  useEffect(() => {
    if (!key || staff?.hidden) return undefined;
    let alive = true;
    let timer = null;
    const load = () => api.get("/staff").then(({ data: d }) => {
      if (!alive) return;
      setData(d);
      timer = setTimeout(load, d.loading ? 4000 : 60000);
    }).catch(() => { if (alive) timer = setTimeout(load, 10000); });
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [key, staff?.hidden]);

  const groups = (data?.groups || []).filter((g) => g.members.length);
  if (!key || staff?.hidden) return null;
  if (!data || data.loading) {
    return (
      <section className="border-t border-eden-border py-16 sm:py-24" aria-labelledby="staff-title" data-testid="staff-section">
        <div className="container-eden">
          <p className="eyebrow mb-4">{t("staff_eyebrow")}</p>
          <h2 id="staff-title" className="text-2xl font-bold tracking-tight text-eden-text sm:text-3xl lg:text-4xl" data-testid="staff-title">{staff.title}</h2>
          <div className="mt-12 space-y-3" role="status" aria-live="polite" data-testid="staff-loading">
            {[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-md bg-eden-surface" style={{ width: `${70 - i * 15}%` }} />)}
            <p className="text-xs text-eden-muted">A ler a equipa no Discord…</p>
          </div>
        </div>
      </section>
    );
  }
  if (!groups.length) return null;
  const onlineCount = groups.reduce((n, g) => n + g.members.filter((m) => m.status !== "offline").length, 0);

  return (
    <section data-reveal className="border-t border-eden-border py-16 sm:py-24" aria-labelledby="staff-title" data-testid="staff-section">
      <div className="container-eden">
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">{t("staff_eyebrow")}</p>
            <h2 id="staff-title" className="text-2xl font-bold tracking-tight text-eden-text sm:text-3xl lg:text-4xl" data-testid="staff-title">{staff.title}</h2>
          </div>
          {staff.text && <p className="text-eden-secondary lg:col-span-6 lg:col-start-7 lg:self-end" data-testid="staff-text">{staff.text}</p>}
        </div>
        {data?.presence_available && (
          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-eden-border px-3 py-1 text-xs text-eden-secondary" data-testid="staff-online-count">
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" aria-hidden="true" /> {onlineCount} {t("staff_online_suffix")}
          </p>
        )}
        <div className="mt-12 divide-y divide-eden-border border-y border-eden-border" data-testid="staff-groups">
          {groups.map((g, i) => (
            <div key={g.id} className="grid gap-4 py-6 animate-fade-up sm:grid-cols-12" style={{ animationDelay: `${i * 60}ms` }} data-testid={`staff-group-${i}`}>
              <div className="flex items-center gap-3 sm:col-span-4">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: hex(g.color) || "rgb(var(--eden-orange))" }} aria-hidden="true" />
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-eden-orange" data-testid={`staff-role-${i}`}>{clean(g.name)}</h3>
                <span className="font-mono text-xs text-eden-muted">{g.members.length}</span>
              </div>
              <ul className="flex flex-wrap gap-2 sm:col-span-8">
                {g.members.map((m) => {
                  const st = STATUS[m.status] || STATUS.offline;
                  return (
                    <li key={m.name} className="inline-flex items-center gap-2 rounded-full border border-eden-border bg-eden-surface px-3.5 py-1.5 text-sm text-eden-text transition-colors duration-200 hover:border-eden-orange" title={st.label} data-testid="staff-member" data-status={m.status}>
                      <span className={`h-2 w-2 shrink-0 rounded-full ${st.cls} ${m.status === "online" ? "shadow-[0_0_6px_rgba(52,211,153,0.9)]" : ""}`} aria-hidden="true" />
                      {m.name}
                      <span className="sr-only">({st.label})</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-eden-muted">{t("staff_note")}</p>
        {staff.apply_enabled !== false && (
          <div className="mt-10 flex flex-col gap-4 rounded-md border border-eden-border bg-eden-surface/60 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="staff-apply">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-eden-orange/15 text-eden-orange"><UserPlus size={18} aria-hidden="true" /></span>
              <div>
                <p className="font-display text-base font-semibold text-eden-text">{staff.apply_label || "Junta-te à equipa"}</p>
                {staff.apply_text && <p className="mt-1 text-sm text-eden-secondary" data-testid="staff-apply-text">{staff.apply_text}</p>}
              </div>
            </div>
            {(staff.apply_mode || "form") === "form" ? (
              <Link to="/candidatura" className="btn-primary h-11 shrink-0" data-testid="staff-apply-button">
                {staff.apply_label || "Junta-te à equipa"} <ArrowUpRight size={16} aria-hidden="true" />
              </Link>
            ) : (
              <a href={staff.apply_url || content.links?.discord_invite} target="_blank" rel="noopener noreferrer" className="btn-primary h-11 shrink-0" data-testid="staff-apply-button">
                {staff.apply_label || "Junta-te à equipa"} <ArrowUpRight size={16} aria-hidden="true" />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
